/**
 * Projetos de correias — o outro tipo de conteúdo do Sistema.
 *
 * Não é pinout nem diagrama: é a correia de sincronismo. Um projeto tem
 * **arquivos** (cada um é uma prancha), e cada arquivo tem **imagens** do
 * trajeto. À parte, o projeto tem **modelos** (veículo, motor, faixa de ano) e
 * os **produtos** em que aparece.
 *
 * **As imagens não aparecem, e a tela diz por quê.** O registro guarda o
 * caminho do arquivo no servidor antigo — `C:\inetpub\…\Img\correias319.jpg` —
 * e a API não serve imagem. É a mesma parede do cache do pinout gráfico: o
 * arquivo mora no IIS antigo, que o host da API não enxerga. Mostrar quantas há
 * e em que ordem é o que dá para fazer com honestidade; inventar uma URL seria
 * mostrar quadro quebrado.
 */

import { useEffect, useState } from "react"
import {
  usarArquivosDeCorreias, usarExcluirArquivoDeCorreias, usarImagensDeCorreias,
  usarModelosDeCorreias, usarOrdenarImagensDeCorreias,
  usarProdutosDeCorreias, usarProjetosDeCorreias, usarPublicarModeloDeCorreias,
  usarSalvarArquivoDeCorreias, usarSalvarProjetoDeCorreias,
  type ArquivoDeCorreias, type ProjetoDeCorreias,
} from "@/api/correias"
import { ErroDaApi } from "@/api/erros"
import { BarraDeEstado } from "@/kit/BarraDeEstado"
import { AvisoDeDadoGlobal } from "@/kit/AvisoDeDadoGlobal"
import { Faixa } from "@/kit/Faixa"
import { usarOrdemDaLista } from "@/kit/ordemDaLista"
import {
  Botao, ColunaRecolhivel, FiltroDaLista, ItemDaLista, Paginacao, usarTermoAdiado,
} from "@/telas/outros/pecas"

const ano = (d: string | null) => {
  if (!d || d.slice(0, 4) === "1900" || d.slice(0, 4) === "0001") return ""
  return d.slice(0, 4)
}

/** Um botão de texto, no tom da tela: ela não usa o kit dos outros projetos. */
function Acao({ children, aoClicar, ocupado, destaque }: {
  children: React.ReactNode
  aoClicar: () => void
  ocupado?: boolean
  destaque?: boolean
}) {
  return (
    <button
      onClick={aoClicar}
      disabled={ocupado}
      style={{
        padding: "5px 11px", borderRadius: 7, fontSize: 12, cursor: ocupado ? "default" : "pointer",
        border: `1px solid ${destaque ? "var(--brand)" : "var(--line2)"}`,
        background: destaque ? "var(--brand)" : "var(--surface)",
        color: destaque ? "var(--brand-on)" : "var(--txt)",
        opacity: ocupado ? 0.6 : 1,
      }}
    >
      {children}
    </button>
  )
}

const POR_PAGINA = 50

/**
 * A lista segue o padrão de Relés e fusíveis: filtro e ordem no topo da coluna, que
 * recolhe e muda de largura. A busca e a ordem vão à API, como em Tabelas de valores —
 * a rota pagina, e ordenar aqui embaralharia só a página aberta.
 */
export function Correias() {
  const [busca, setBusca] = useState("")
  const termo = usarTermoAdiado(busca)
  const [ordem, setOrdem] = usarOrdemDaLista("correias")
  const [pagina, setPagina] = useState(1)
  // Busca ou ordem nova começam da primeira página.
  useEffect(() => setPagina(1), [termo, ordem])
  const projetos = usarProjetosDeCorreias({
    busca: termo || undefined, ordem: ordem === "alfabetica" ? "alfabetica" : undefined,
    pagina, pageSize: POR_PAGINA,
  })
  const [aberto, setAberto] = useState<ProjetoDeCorreias | null>(null)
  const [incluindo, setIncluindo] = useState(false)
  const [recado, setRecado] = useState<string | null>(null)

  const lista = projetos.data ?? []

  return (
    <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      <Faixa titulo="Projetos de correias" detalhe={`${projetos.total.toLocaleString("pt-BR")} projetos`}>
        <Botao destaque aoClicar={() => { setRecado(null); setIncluindo(true) }}>
          Incluir projeto
        </Botao>
      </Faixa>

      <AvisoDeDadoGlobal oQueMuda="Projeto de correias não pertence a projeto de pinout" />

      {recado && (
        <p role="status" style={{
          margin: 0, padding: "8px 18px", fontSize: 12, background: "var(--surface2)",
          borderBottom: "1px solid var(--line)", color: "var(--txt2)",
        }}>
          {recado}
        </p>
      )}

      {incluindo && (
        <NovoProjeto
          aoFechar={() => setIncluindo(false)}
          aoCriar={(p) => {
            setIncluindo(false)
            setAberto(p)                    // abre o que acabou de nascer
            setRecado(`Projeto ${p.id} criado. Inclua os arquivos das pranchas abaixo.`)
          }}
          aoErrar={setRecado}
        />
      )}

      <div style={{ flex: 1, minHeight: 0, display: "flex" }}>
        <ColunaRecolhivel chave="correias" titulo="Projetos" largura={300}
                          detalhe={termo ? `${projetos.total.toLocaleString("pt-BR")} achados` : undefined}
                          topo={<FiltroDaLista valor={busca} aoMudar={setBusca} rotulo="Filtrar projetos"
                                               placeholder="Descrição ou número…"
                                               ordem={ordem} aoMudarOrdem={setOrdem} />}>
          {projetos.isPending ? (
            <p style={{ fontSize: 12.5, color: "var(--txt2)" }}>Carregando…</p>
          ) : projetos.error ? (
            <p style={{ fontSize: 12.5, color: "var(--erro)" }}>
              {projetos.error instanceof ErroDaApi
                ? projetos.error.message : "Não foi possível ler os projetos."}
            </p>
          ) : lista.length === 0 ? (
            <p style={{ fontSize: 12.5, color: "var(--txt3)" }}>{termo ? "Nada com esse texto." : "Nenhum projeto."}</p>
          ) : (
            <>
              {lista.map((p) => (
                <ItemDaLista
                  key={p.id}
                  ativo={aberto?.id === p.id}
                  aoClicar={() => setAberto(p)}
                  titulo={p.descricao?.trim() || "(sem descrição)"}
                  detalhe={`projeto ${p.id}${p.atualizar ? " · marcado para reprocessar" : ""}`}
                />
              ))}
              <Paginacao pagina={pagina} aoMudar={setPagina}
                         temProxima={pagina < Math.ceil(projetos.total / POR_PAGINA)}
                         totalDePaginas={Math.max(1, Math.ceil(projetos.total / POR_PAGINA))} />
            </>
          )}
        </ColunaRecolhivel>

        <div style={{ flex: 1, minWidth: 0, overflowY: "auto" }}>
          {!aberto ? (
            <p style={{ margin: 18, fontSize: 13, color: "var(--txt2)", maxWidth: "48ch", lineHeight: 1.6 }}>
              Escolha um projeto à esquerda.
            </p>
          ) : (
            <Detalhe projeto={aberto} />
          )}
        </div>
      </div>

      <BarraDeEstado>
        {projetos.total > 0 && (
          <span>
            <b className="num" style={{ color: "var(--txt)" }}>{projetos.total.toLocaleString("pt-BR")}</b>{" "}
            {projetos.total === 1 ? "projeto de correias" : "projetos de correias"}
          </span>
        )}
        <span style={{ color: "var(--txt3)" }}>
          As imagens ficam no servidor antigo — a API não as serve.
        </span>
      </BarraDeEstado>
    </div>
  )
}

const estiloCampo: React.CSSProperties = {
  padding: "6px 9px", borderRadius: 7, border: "1px solid var(--line2)",
  background: "var(--surface)", color: "var(--txt)", fontSize: 12.5,
}

/** O que a API devolve quando recusa, em português, sem "Request failed". */
const recadoDoErro = (e: unknown, quando: string) =>
  e instanceof ErroDaApi && e.message ? e.message : `Não foi possível ${quando}.`

/**
 * Incluir projeto de correias.
 *
 * O formulário pede só a descrição, que é o único campo obrigatório do
 * `POST api/projetos-correias` — o resto (arquivos, modelos, produtos) é filho e
 * só existe depois de o projeto existir. Por isso, ao gravar, a tela **abre o
 * projeto criado** em vez de voltar para a lista: quem inclui está começando um
 * trabalho, não terminando.
 *
 * Nada aqui é novo na API: os hooks de gravar já estavam escritos em
 * `api/correias.ts` e nenhuma tela os importava.
 */
function NovoProjeto({ aoCriar, aoFechar, aoErrar }: {
  aoCriar: (p: ProjetoDeCorreias) => void
  aoFechar: () => void
  aoErrar: (m: string) => void
}) {
  const salvar = usarSalvarProjetoDeCorreias()
  const [descricao, setDescricao] = useState("")

  const gravar = () => {
    const texto = descricao.trim()
    if (!texto) return
    salvar.mutate({ id: null, descricao: texto }, {
      onSuccess: (p) => { if (p) aoCriar(p); else aoFechar() },
      onError: (e) => aoErrar(recadoDoErro(e, "criar o projeto")),
    })
  }

  return (
    <div style={{
      padding: "12px 18px", borderBottom: "1px solid var(--line)", background: "var(--surface2)",
      display: "flex", alignItems: "flex-end", gap: 10, flexWrap: "wrap",
    }}>
      <label style={{ display: "flex", flexDirection: "column", gap: 4, flex: "1 1 320px" }}>
        <span style={{ fontSize: 11.5, color: "var(--txt2)" }}>Descrição do projeto</span>
        <input
          autoFocus
          value={descricao}
          onChange={(e) => setDescricao(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") gravar() }}
          placeholder="Ex.: Correia de sincronismo — Fiat Fire 1.0"
          style={estiloCampo}
        />
      </label>
      <Acao destaque ocupado={salvar.isPending || !descricao.trim()} aoClicar={gravar}>
        {salvar.isPending ? "Criando…" : "Criar projeto"}
      </Acao>
      <Acao aoClicar={aoFechar}>Cancelar</Acao>
    </div>
  )
}

function Detalhe({ projeto }: { projeto: ProjetoDeCorreias }) {
  const arquivos = usarArquivosDeCorreias(projeto.id)
  const modelos = usarModelosDeCorreias(projeto.id)
  const produtos = usarProdutosDeCorreias(projeto.id)
  const [arquivoAberto, setArquivoAberto] = useState<ArquivoDeCorreias | null>(null)

  const salvarArquivo = usarSalvarArquivoDeCorreias(projeto.id)
  const excluirArquivo = usarExcluirArquivoDeCorreias(projeto.id)
  const publicarModelo = usarPublicarModeloDeCorreias(projeto.id)
  const [erroDoModelo, setErroDoModelo] = useState<string | null>(null)
  const [novoArquivo, setNovoArquivo] = useState("")
  const [erro, setErro] = useState<string | null>(null)

  const incluirArquivo = () => {
    const texto = novoArquivo.trim()
    if (!texto) return
    setErro(null)
    salvarArquivo.mutate({ id: null, descricao: texto }, {
      onSuccess: () => setNovoArquivo(""),
      onError: (x) => setErro(recadoDoErro(x, "incluir o arquivo")),
    })
  }

  return (
    <div style={{ padding: 18, display: "flex", flexDirection: "column", gap: 20, maxWidth: 780 }}>
      <div>
        <h2 style={{ margin: "0 0 3px", fontSize: 15, fontWeight: 600 }}>{projeto.descricao}</h2>
        <span style={{ fontSize: 11.5, color: "var(--txt3)" }}>
          projeto <span className="num">{projeto.id}</span>
          {projeto.atualizar && " · marcado para reprocessar"}
        </span>
      </div>

      <section>
        <h3 style={{ margin: "0 0 8px", fontSize: 13, fontWeight: 600 }}>
          Arquivos{" "}
          <span style={{ fontWeight: 400, color: "var(--txt3)" }}>
            ({(arquivos.data ?? []).length})
          </span>
        </h3>
        {arquivos.isPending ? (
          <p style={{ margin: 0, fontSize: 12, color: "var(--txt2)" }}>Carregando…</p>
        ) : (arquivos.data ?? []).length === 0 ? (
          <p style={{ margin: 0, fontSize: 12, color: "var(--txt3)" }}>Nenhum arquivo.</p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {(arquivos.data ?? []).map((a) => (
              <div key={a.id} style={{
                border: `1px solid ${arquivoAberto?.id === a.id ? "var(--brand)" : "var(--line2)"}`,
                borderRadius: 9, background: "var(--surface)", overflow: "hidden",
              }}>
                <div style={{ display: "flex", alignItems: "center" }}>
                <button
                  onClick={() => setArquivoAberto(arquivoAberto?.id === a.id ? null : a)}
                  aria-expanded={arquivoAberto?.id === a.id}
                  style={{
                    display: "flex", alignItems: "center", gap: 9, flex: 1, minWidth: 0,
                    padding: "9px 11px", textAlign: "left",
                  }}
                >
                  <span aria-hidden="true" style={{ fontSize: 10, color: "var(--txt3)" }}>
                    {arquivoAberto?.id === a.id ? "▾" : "▸"}
                  </span>
                  <span style={{ flex: 1, minWidth: 0, fontSize: 12, lineHeight: 1.4 }}>
                    {a.descricao?.trim() || "(sem descrição)"}
                  </span>
                  <span style={{ fontSize: 10.5, color: "var(--txt3)", whiteSpace: "nowrap" }}>
                    {a.modeloIds.length} mod · {a.sistemaIds.length} sist
                  </span>
                </button>
                {/* Irmão do botão que abre, e não filho dele: botão dentro de botão é
                    HTML inválido, e o teclado para de alcançar um dos dois. */}
                <button
                  aria-label={`Excluir o arquivo ${a.descricao?.trim() || a.id}`}
                  disabled={excluirArquivo.isPending}
                  onClick={() => {
                    setErro(null)
                    excluirArquivo.mutate(a.id, {
                      onSuccess: () => { if (arquivoAberto?.id === a.id) setArquivoAberto(null) },
                      onError: (x) => setErro(recadoDoErro(x, "excluir o arquivo")),
                    })
                  }}
                  style={{
                    flex: "0 0 auto", padding: "0 11px", fontSize: 11, color: "var(--txt3)",
                    background: "transparent", border: 0, cursor: "pointer",
                  }}
                >
                  excluir
                </button>
                </div>
                {arquivoAberto?.id === a.id && <Imagens arquivoId={a.id} />}
              </div>
            ))}
          </div>
        )}

        {/* Incluir arquivo fica DENTRO da seção, e não numa tela à parte: a prancha
            não existe fora do projeto, e o caminho de quem acabou de criar um projeto
            é acrescentar a primeira. */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}>
          <input
            value={novoArquivo}
            onChange={(e) => setNovoArquivo(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") incluirArquivo() }}
            placeholder="Descrição da prancha"
            style={{ ...estiloCampo, flex: "1 1 240px" }}
          />
          <Acao
            ocupado={salvarArquivo.isPending || !novoArquivo.trim()}
            aoClicar={incluirArquivo}
          >
            {salvarArquivo.isPending ? "Incluindo…" : "Incluir arquivo"}
          </Acao>
        </div>
        {erro && (
          <p role="status" style={{ margin: "6px 0 0", fontSize: 11.5, color: "var(--erro)" }}>
            {erro}
          </p>
        )}
      </section>

      <section>
        <h3 style={{ margin: "0 0 8px", fontSize: 13, fontWeight: 600 }}>
          Modelos{" "}
          <span style={{ fontWeight: 400, color: "var(--txt3)" }}>
            ({(modelos.data ?? []).length})
          </span>
        </h3>
        {(modelos.data ?? []).length === 0 ? (
          <p style={{ margin: 0, fontSize: 12, color: "var(--txt3)" }}>Nenhum modelo.</p>
        ) : (
          <div style={{ border: "1px solid var(--line2)", borderRadius: 9, overflow: "hidden" }}>
            {(modelos.data ?? []).map((m, i) => (
              <div key={m.id} style={{
                display: "flex", alignItems: "center", gap: 10, padding: "8px 11px", fontSize: 12,
                borderTop: i === 0 ? undefined : "1px solid var(--line)", background: "var(--surface)",
              }}>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <b>{m.descricao}</b> {m.veiculoModelo}
                  <span style={{ display: "block", fontSize: 11, color: "var(--txt3)" }}>
                    {m.motorNome}
                  </span>
                </span>
                <span className="num" style={{ fontSize: 11, color: "var(--txt3)", whiteSpace: "nowrap" }}>
                  {[ano(m.anoInicial), ano(m.anoFinal)].filter(Boolean).join("–")}
                </span>
                {/* A publicação é do modelo: sem este botão, o modelo cadastrado aqui
                    ficava fora do ar sem nenhuma tela que o pusesse no ar. */}
                <span style={{
                  fontSize: 10.5, fontWeight: 600, whiteSpace: "nowrap",
                  color: m.publicado ? "var(--ok, #2e7d32)" : "var(--txt3)",
                }}>
                  {m.publicado ? "● no ar" : "○ fora do ar"}
                </span>
                <Acao
                  destaque={!m.publicado}
                  ocupado={publicarModelo.isPending}
                  aoClicar={() => {
                    setErroDoModelo(null)
                    publicarModelo.mutate({ id: m.id, publicar: !m.publicado }, {
                      onError: (x) => setErroDoModelo(recadoDoErro(x, m.publicado ? "tirar do ar" : "publicar")),
                    })
                  }}
                >
                  {m.publicado ? "Tirar do ar" : "Publicar"}
                </Acao>
              </div>
            ))}
          </div>
        )}
        {erroDoModelo && (
          <p role="status" style={{ margin: "6px 0 0", fontSize: 11.5, color: "var(--erro)" }}>
            {erroDoModelo}
          </p>
        )}
      </section>

      <section>
        <h3 style={{ margin: "0 0 8px", fontSize: 13, fontWeight: 600 }}>Produtos</h3>
        {(produtos.data ?? []).length === 0 ? (
          <p style={{ margin: 0, fontSize: 12, color: "var(--txt3)" }}>Nenhum produto vinculado.</p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            {(produtos.data ?? []).map((p) => (
              <span key={p.produtoId} style={{
                display: "flex", alignItems: "center", gap: 9, padding: "7px 11px",
                borderRadius: 8, background: "var(--surface2)", fontSize: 12,
              }}>
                <span style={{ flex: 1 }}>{p.produtoNome}</span>
                {p.publicado && (
                  <span style={{
                    fontSize: 10.5, fontWeight: 600, padding: "1px 8px", borderRadius: 999,
                    background: "var(--ok-soft)", color: "var(--ok-on-soft)",
                  }}>
                    publicado
                  </span>
                )}
              </span>
            ))}
          </div>
        )}
      </section>
    </div>
  )
}

/**
 * As imagens do arquivo — pelo que se sabe delas, que não é a imagem.
 *
 * O registro guarda o caminho no servidor antigo. Mostrar a ordem e o nome do
 * arquivo é honesto; montar uma URL a partir daquele caminho seria chutar, e o
 * resultado seria um quadro quebrado em vez de uma explicação.
 */
function Imagens({ arquivoId }: { arquivoId: number }) {
  const imagens = usarImagensDeCorreias(arquivoId)
  const ordenar = usarOrdenarImagensDeCorreias(arquivoId)
  const lista = imagens.data ?? []

  /**
   * Move uma imagem uma casa. **A ordem é metadado**, e é por isso que este
   * botão funciona enquanto subir imagem não: a escrita não toca no servidor de
   * arquivos, só na coluna.
   */
  function mover(indice: number, passo: -1 | 1) {
    const destino = indice + passo
    if (destino < 0 || destino >= lista.length) return

    const ids = lista.map((x) => x.id)
    const t = ids[indice]!
    ids[indice] = ids[destino]!
    ids[destino] = t
    ordenar.mutate(ids)
  }

  return (
    <div style={{ padding: "0 11px 11px 30px", borderTop: "1px solid var(--line)" }}>
      {imagens.isPending ? (
        <p style={{ margin: "9px 0 0", fontSize: 11.5, color: "var(--txt2)" }}>Carregando…</p>
      ) : lista.length === 0 ? (
        <p style={{ margin: "9px 0 0", fontSize: 11.5, color: "var(--txt3)" }}>
          Este arquivo não tem imagem.
        </p>
      ) : (
        <>
          <p style={{ margin: "9px 0 6px", fontSize: 11, color: "var(--acao-on-soft)", lineHeight: 1.5 }}>
            <b>{lista.length} {lista.length === 1 ? "imagem" : "imagens"}</b>, e elas não aparecem
            aqui: o arquivo mora no servidor antigo e a API não serve imagem. Abaixo, o que se sabe
            de cada uma.
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {lista.map((im, i) => (
              <span key={im.id} style={{ display: "flex", gap: 8, fontSize: 11, alignItems: "center" }}>
                <span className="num" style={{ flex: "0 0 22px", color: "var(--txt3)" }}>{im.ordem}</span>
                <span className="mono" style={{
                  flex: 1, minWidth: 0, color: "var(--txt2)",
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}>
                  {/* Só o nome do arquivo: o caminho inteiro é do servidor de
                      alguém e não ajuda quem está olhando a tela. */}
                  {(im.descricao ?? "").split(/[\\/]/).pop() || im.descricao}
                </span>
                <button
                  onClick={() => mover(i, -1)}
                  disabled={i === 0 || ordenar.isPending}
                  aria-label="Subir"
                  style={botaoDeOrdem}
                >
                  ↑
                </button>
                <button
                  onClick={() => mover(i, 1)}
                  disabled={i === lista.length - 1 || ordenar.isPending}
                  aria-label="Descer"
                  style={botaoDeOrdem}
                >
                  ↓
                </button>
              </span>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

const botaoDeOrdem: React.CSSProperties = {
  border: "1px solid var(--line)", borderRadius: 4, background: "transparent",
  color: "var(--txt2)", cursor: "pointer", fontSize: 10, lineHeight: 1,
  padding: "1px 5px",
}
